import React, { useState } from "react";
import styled from "@emotion/styled";

import { InlineSaveListButton } from "components/entities/save_components/lists/InlineSaveListButton";
import { appRoutes, SupplierContext } from "lib/appRoutes";
import { EllipsisTooltipRouterLink } from "lib/core_components/EllipsisTooltip";
import { EventData, TrackingProvider } from "lib/tracking";

/** Minimal buyer-list shape needed by NameColumn — a subset of the full BuyerList type. */
type BuyerListSummary = { id: string; name: string };

function BuyerNameLink({
  buyerGuid,
  buyerName,
  isSupplierRelationship,
  supplierContext,
}: {
  buyerGuid: string;
  buyerName: string;
  isSupplierRelationship: boolean;
  supplierContext?: SupplierContext;
}) {
  const target =
    isSupplierRelationship && supplierContext
      ? appRoutes.supplierBuyer(supplierContext, buyerGuid)
      : appRoutes.buyerDetail(buyerGuid);

  return (
    <StyledEllipsisTooltipRouterLink
      noWrap
      fullText={buyerName}
      linkText={buyerName}
      linkProps={target}
    />
  );
}

export function NameColumn({
  buyerGuid,
  buyerName,
  isSupplierRelationship,
  supplierContext,
  trackingData,
  savedLists,
}: {
  buyerGuid: string;
  buyerName: string;
  isSupplierRelationship: boolean;
  /**
   * Supplier-profile context. When set with
   * `isSupplierRelationship`, the buyer name links to the buyer↔supplier relationship subpage.
   * When omitted, the link falls back to the standalone buyer profile — every current caller that
   * sets `isSupplierRelationship` also sets this, so the fallback is defensive only.
   */
  supplierContext?: SupplierContext;
  trackingData?: EventData;
  /** Pre-fetched buyer lists; when provided skips the `useBuyersInLists` fallback. */
  savedLists?: BuyerListSummary[];
}) {
  const savedListNames = (savedLists ?? []).map((l) => l.name);

  // State is used to show the save buyer list button on mouse entering the first cell
  const [mouseEntered, setMouseEntered] = useState(false);

  return (
    <TrackingProvider data={{ "Buyer name": buyerName, ...trackingData }}>
      <NameColumnContainer
        onMouseEnter={() => setMouseEntered(true)}
        onMouseLeave={() => setMouseEntered(false)}
      >
        <BuyerNameContainer onClick={(e) => e.stopPropagation()}>
          <BuyerNameLink
            buyerGuid={buyerGuid}
            buyerName={buyerName}
            isSupplierRelationship={isSupplierRelationship}
            supplierContext={supplierContext}
          />
        </BuyerNameContainer>
        <div
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
          }}
        >
          <InlineSaveListButton
            entityType="buyer"
            entityIds={[buyerGuid]}
            displayType={mouseEntered ? "button" : "showSavedLists"}
            savedListNames={savedListNames}
          />
        </div>
      </NameColumnContainer>
    </TrackingProvider>
  );
}

// minmax(0, 1fr) lets the name column shrink below its text width, so long names truncate
// instead of pushing the save button onto a second line.
const NameColumnContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  line-height: 32px;
`;

const BuyerNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledEllipsisTooltipRouterLink = styled(EllipsisTooltipRouterLink)`
  color: ${({ theme }) => theme.colors.sysPrimaryDefault};
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.colors.sysPrimaryHover};
  }
`;
